import { useAuth } from '@teko/auth-react';
import {
  Body,
  Flex,
  SubTitle,
  themeColor,
  useLoading,
  useModal,
} from '@teko/ds-desktop';
import { FavouriteAlternativeOutlined, FavouriteFilled } from '@teko/ds-icon';
import { getTrackingData } from '@teko/tracking';
import { cleanObject } from '@teko/utils';
import { t } from 'i18n';
import Link from 'next/link';
import { useMemo } from 'react';
import { useServices, useTenantConfig, useToast } from '../contexts';

interface Props {
  sku: string;
  size?: string;
}

export const WishlistButton = ({ sku, size = '26' }: Props) => {
  const { user, setUser, authService } = useAuth();
  const { showModal } = useModal();
  const { userService } = useServices();
  const [, setLoading] = useLoading('wishListButton');

  const { isMobile, appName } = useTenantConfig();
  const toast = useToast(isMobile);
  const isWishList = useMemo(
    () => !!user?.metaData?.wishList?.includes(sku),
    [sku, user?.metaData?.wishList],
  );

  const handleWhenNotLogin = () => {
    showModal({
      size: 'm',
      title: t('Thông báo'),
      centeredTitle: true,
      okText: t('Đăng nhập'),
      cancelText: t('Đóng'),
      children: (
        <Flex direction="column" align="center">
          <Body marginTop="s4">
            {t(
              'Vui lòng đăng nhập tài khoản {{appName}} để thêm sản phẩm yêu thích',
              {
                appName: appName || '',
              },
            )}
          </Body>
        </Flex>
      ),
      onOk: () => authService.login(),
    });
  };

  const onClickWishList = async () => {
    try {
      if (!user?.userId) {
        handleWhenNotLogin();
        return;
      }
      setLoading(true);
      const previousMetaData = user?.metaData || {};
      const previousWishlist = previousMetaData?.wishList || [];
      const listSku = [sku, ...previousWishlist];
      const diskListSku = previousWishlist.filter((item) => item !== sku);
      const profile = await userService.updateProfile({
        metaData: {
          ...previousMetaData,
          wishList: isWishList ? diskListSku : listSku,
        },
      });
      const updatedProfile = {
        ...user,
        ...cleanObject(profile),
      };
      setUser(updatedProfile);
      setLoading(false);

      if (updatedProfile?.metaData?.wishList.includes(sku)) {
        toast.success?.({
          title: '',
          message: '',
          duration: 3000,
          closeable: false,
          [isMobile ? 'message' : 'title']: (
            <SubTitle level={2}>
              {t('Thêm thành công vào mục "Sản phẩm yêu thích". ')}
              <Link href="/account/wish-list">
                <span style={{ color: themeColor.link500 }} className="text">
                  {t('Xem ngay')}
                </span>
              </Link>
            </SubTitle>
          ),
        });
      } else {
        toast.success?.({
          title: '',
          message: '',
          duration: 3000,
          closeable: false,
          [isMobile ? 'message' : 'title']: t(
            'Xoá thành công khỏi mục "Sản phẩm yêu thích."',
          ),
        });
      }
    } catch (error) {
      setLoading(false);
      toast.error?.({
        title: '',
        message: '',
        duration: 3000,
        closeable: false,
        [isMobile ? 'message' : 'title']: t('Có lỗi xảy ra'),
      });
    }
  };

  return (
    <>
      {isWishList ? (
        <FavouriteFilled
          onClick={(event) => {
            event.preventDefault();
            onClickWishList();
          }}
          size={size}
          color="primary"
          style={{ cursor: 'pointer' }}
          {...getTrackingData({
            isTrackContent: true,
            contentName: 'removeWishlist',
            payload: sku,
          })}
        />
      ) : (
        <FavouriteAlternativeOutlined
          onClick={(event) => {
            event.preventDefault();
            onClickWishList();
          }}
          size={size}
          color="primary"
          style={{ cursor: 'pointer' }}
          {...getTrackingData({
            isTrackContent: true,
            contentName: 'addWishlist',
            payload: sku,
          })}
        />
      )}
    </>
  );
};
